import ReportingService from "../../../src/services/ReportingService";
import { ReportingDefaults } from "../../../src/services/ReportingDefaults";
import {
  baseInternetOffersData_RR,
  baseSatelliteOffersData_RR,
  isZipcodeDependentCardPrices,
  getMultiOfferDuration,
  packageNameNormalizing
} from "../../../src/components/video-lab/plan-cards/utils";
import { ComponentNames } from "../../constant";
import { addPackageDetailsToEmbeddedSvods } from "../../helper/svod-utils";
import { logAdapterEvent, logError } from "../../helper/logger";
import { getComparePkgTrayData } from "../../helper/ComparePkgTray";
/**
 * @description Inject prices into card copy indicators that do not change in
 * the lifecycle of the cards.
 * @returns
 */

const ADAPTER_NAME = "planCardAdapter";

const getChannelCount = (productName, packageConfig, isSatelliteFlag) => {
  const key = productName?.toLowerCase()?.trim();
  const packageKey = packageConfig?.filter(pkg => pkg?.queryparam === key);

  const filteredPackage = isSatelliteFlag ?
    packageKey?.find(pkg => pkg?.id?.startsWith("DTV_")) :
    packageKey?.find(pkg => pkg?.id?.startsWith("ATT_TV_"));
  const channelCount =
    (filteredPackage?.channel?.displayCount || "") +
      (filteredPackage?.channel?.signLetter || "") || "";
  return channelCount;
};

interface IPlanCardAdapter {
  content: any;
  getOffersData: any;
  getOffersDTVsData: any;
  packageConfig: any;
  index: number;
  cms_type?: string;
  embeddedSvods: any;
  dtviEmbeddedSvodOffers: any;
  dtvsEmbeddedSvodOffers: any;
  cmsPageName?: string;
  byodPlanCardOffers?: any;
  affiliatePartner?: string;
  genreSvodOffers?: any;
}
/** * @description Adapts the plan card content for rendering.
 * @param {Object} params - The parameters for the adapter function.
 * @param {Object} params.content - The content object containing plan card data.
 * @param {Object} params.getOffersData - Data for offers, including prices and benefits.
 * @param {Object} params.getOffersDTVsData - Data for DTV offers, including prices.
 * @param {Object} params.packageConfig - Configuration for packages, including channel counts.
 * @param {number} params.index - The index of the plan card in the list.
 * @param {string} params.cms_type - The type of CMS used (e.g., "aem" or "storyblok").
 * @param {Array} params.embeddedSvods - Array of embedded SVODs.
 * @param {Function} params.dtviEmbeddedSvodOffers - Function to get embedded SVODs for DTV.
 * @returns {Object} - An object containing the adapted plan card data, including props, type, and configuration.
 */

// Generated by Copilot- Start - 2026-02-21 00:52:10
const getOfferLegal = (
  benefitsArr: { discount: number; periodMo: number; code: string }[],
  offerPart?: string
) => {
  if (!offerPart) {
    return "";
  }
  if (!benefitsArr || benefitsArr.length === 0) {
    // No benefits to resolve — static-passthrough if the template has no
    // tokens, otherwise drop it (its placeholders cannot be filled).
    return /\[[^\]]+\]/.test(offerPart) ? "" : offerPart;
  }
  const sorted = [...benefitsArr].sort(
    (a, b) => (a.periodMo || 0) - (b.periodMo || 0)
  );

  let resolved = offerPart;

  if (sorted.length === 1) {
    const benefit = sorted[0];
    const discountVal = Math.round(benefit.discount ?? 0);
    const durationMo = benefit.periodMo ?? 0;

    if (discountVal <= 0 || durationMo <= 0) {
      return "";
    }

    const durationText = getMultiOfferDuration(null, durationMo);

    resolved = resolved
      .replace("[first_offer]", String(discountVal))
      .replace("[first_offer_duration]", durationText);
  }  else {
    // Multiple benefits: first tier = sum of all discounts in first month
    const firstMonthDiscount = sorted.reduce(
      (sum, b) => sum + (b.discount ?? 0),
      0
    );
    const firstBenefitPeriod = sorted[0]?.periodMo ?? 1;

    resolved = resolved
      .replace("[first_offer]", String(Math.round(firstMonthDiscount)))
      .replace(
        "[first_offer_duration]",
        getMultiOfferDuration(1, firstBenefitPeriod)
      );

    // Second tier (legacy behavior): whatever was previously "second_offer"
    // We keep the logic but we don't depend on hard-coded ordinals for cleanup.
    if (sorted.length > 1) {
      const nextBenefit = sorted[1];
      const discountVal = Math.round(nextBenefit.discount ?? 0);
      const periodMo = nextBenefit.periodMo ?? 0;

      if (discountVal > 0 && periodMo > firstBenefitPeriod) {
        const startMonth = firstBenefitPeriod + 1;
        const endMonth = periodMo;

        resolved = resolved
          .replace("[second_offer]", String(discountVal))
          .replace(
            "[second_offer_duration]",
            getMultiOfferDuration(startMonth, endMonth)
          );
      }
    }
  }

  // ---- Generic cleanup using endsWith("_offer") / endsWith("_offer_duration") ----
  // Remove any leftover $[xxx_offer]... text and bare [xxx_offer_duration] tokens
  const tokenPattern = /\[([^\]]+)\]/g;
  const tokens = [...resolved.matchAll(tokenPattern)].map(m => m[1]);

  for (const token of tokens) {
    if (token.endsWith("_offer")) {
      // Remove the whole "$[token] ..." segment
      const ph = `[${token}]`;
      resolved = resolved.replace(
        new RegExp(`\\$?${ph.replace(/[[\]]/g, "\\$&")}[^\\n]*`, "gi"),
        ""
      );
    } else if (token.endsWith("_offer_duration")) {
      // Just strip the duration placeholder itself
      const ph = `[${token}]`;
      resolved = resolved.replace(
        new RegExp(ph.replace(/[[\]]/g, "\\$&"), "g"),
        ""
      );
    }
  }
  return resolved;
}

/**
 * Shared low‑level resolver: *_req_price, *_offer_duration, [first_offer], [second_offer]
 * Uses existing logic (req price + duration + single‑benefit cleanup).
 */
// Generated by Copilot- Start - 2026-02-25 13:32:58
const applyOfferPlaceholders = (
  {
    content,
    productNameKey,
    productType,
    offerPriceNum,
    geminiFeeNum,
    sortedBenefits,
    maxPeriodMo,
    consideredBenefits,
    card,
    evaluateReqPriceFormula
  }: {
    content: string;
    productNameKey: string;
    productType: string;
    offerPriceNum: number;
    geminiFeeNum: number;
    sortedBenefits: Array<{ discount: number; periodMo: number; code: string }>;
    maxPeriodMo: number | null;
    consideredBenefits: Array<{ discount: number; periodMo: number; code: string }>;
    card: any;
    evaluateReqPriceFormula: (
      priceKey: string,
      requiredPriceEvalContext: { baseOffer: number; deviceFee: number; useRsnFeeLocal: boolean },
      consideredBenefits: Array<{ code: string; discount: number; periodMo: number }>,
      tierLevel?: number
    ) => { basePriceExcludingRsn: number; includesRsnFee: boolean };
  }
): string => {
  let resolvedContent = content ?? "";

  const hasReqPricePlaceholders = /\[([^\]]*_req_price)\]/.test(resolvedContent);
  const hasDurationPlaceholders = /\[([^\]]*_offer_duration)\]/.test(resolvedContent);
  const hasOfferAmountPlaceholders =
    resolvedContent?.includes("[first_offer]") || resolvedContent?.includes("[second_offer]");

  if (!(hasReqPricePlaceholders || hasDurationPlaceholders || hasOfferAmountPlaceholders)) {
    return resolvedContent;
  }

  // 1) Fill [first_offer], [second_offer] amounts
  if (hasOfferAmountPlaceholders && sortedBenefits.length > 0) {
    // First tier: sum of discounts in first period
    const firstPeriod = sortedBenefits[0]?.periodMo ?? 1;
    let firstMonthDiscount = 0;

    if (sortedBenefits.length === 1) {
      firstMonthDiscount = sortedBenefits[0].discount ?? 0;
    } else {
      firstMonthDiscount = sortedBenefits.reduce(
        (sum, b) => sum + (b.discount ?? 0),
        0
      );
    }

    const firstDuration =
      sortedBenefits?.length === 1 ?
        getMultiOfferDuration(null, firstPeriod) :
        getMultiOfferDuration(1, firstPeriod);

    resolvedContent = resolvedContent
      .replace(/\[first_offer\]/g, String(Math.round(firstMonthDiscount)))
      .replace(/\[first_offer_duration\]/g, firstDuration || "");

    // Second tier, if there is a second benefit
    if (sortedBenefits?.length > 1) {
      const second = sortedBenefits[1];
      const secondDiscount = Math.round(second.discount ?? 0);
      const secondPeriod = second.periodMo ?? firstPeriod;

      if (secondDiscount > 0 && secondPeriod > firstPeriod) {
        const secondDuration = getMultiOfferDuration(firstPeriod + 1, secondPeriod);
        resolvedContent = resolvedContent
          .replace(/\[second_offer\]/g, String(secondDiscount))
          .replace(/\[second_offer_duration\]/g, secondDuration || "");
      }
    } else if (maxPeriodMo && maxPeriodMo > 1) {
      // One benefit but longer overall period
      const secondDuration = getMultiOfferDuration(2, maxPeriodMo);
      resolvedContent = resolvedContent?.replace(
        /\[second_offer_duration\]/g,
        secondDuration || ""
      );
    }
  }

  // 2) Fill *_req_price using evaluateReqPriceFormula
  if (hasReqPricePlaceholders) {
    const reqPriceMatches = [
      ...(resolvedContent.matchAll(/\[([^\]]*_req_price)\]/g) || [])
    ];

    if (reqPriceMatches.length) {
      const useRsnFeeLocalInline = ![
        "ENTERTAINMENT",
        "MÁS LATINO™",
        "MAS LATINO",
        "ÓPTIMO MÁS™",
        "OPTIMO MAS"
      ]?.some(name => productNameKey?.includes?.(name));

      const baseOfferInline = offerPriceNum || 0;
      const deviceFeeInline = productType === "byod" ? 0 : geminiFeeNum || 0;
      const requiredPriceEvalContextInline = {
        baseOffer: baseOfferInline,
        deviceFee: deviceFeeInline,
        useRsnFeeLocal: useRsnFeeLocalInline
      };

      const requiredPriceValuesExcludingRsnInline: Record<string, number> =
        (card as any)._requiredPriceValuesNoRsn || {};
      const reqPricePlaceholdersWithRsnInline: string[] =
        (card as any)._reqPricePlaceholdersWithRsn || [];

      reqPriceMatches.forEach((match, idx) => {
        const reqKey = match[1];
        const { basePriceExcludingRsn, includesRsnFee } =
          evaluateReqPriceFormula(
            reqKey,
            requiredPriceEvalContextInline,
            sortedBenefits,
            idx
          );

        requiredPriceValuesExcludingRsnInline[reqKey] = basePriceExcludingRsn;
        if (includesRsnFee) {
          reqPricePlaceholdersWithRsnInline.push(reqKey);
        }

        if (!includesRsnFee && basePriceExcludingRsn > 0) {
          const inlineVal = basePriceExcludingRsn.toFixed(2);
          const placeholderRegex = new RegExp(`\\[${reqKey}\\]`, "g");
          resolvedContent = resolvedContent.replace(placeholderRegex, inlineVal);
        }
      });

      (card as any)._requiredPriceValuesNoRsn =
        requiredPriceValuesExcludingRsnInline;
      (card as any)._reqPricePlaceholdersWithRsn =
        reqPricePlaceholdersWithRsnInline;
    }
  }

  if (hasDurationPlaceholders) {
    const durMatches = [
      ...(resolvedContent.matchAll(/\[([^\]]*_offer_duration)\]/g) || [])
    ];

    durMatches.forEach(match => {
      const durKey = match[1]; // e.g., "first_offer_duration"
      let durationValue: string | null = null;

      if (durKey === "first_offer_duration") {
        const firstPeriod = sortedBenefits[0]?.periodMo ?? 1;
        durationValue =
          sortedBenefits.length === 1 ?
            getMultiOfferDuration(null, firstPeriod) :
            getMultiOfferDuration(1, firstPeriod);
      } else if (durKey === "second_offer_duration") {
        if (sortedBenefits.length > 1) {
          const firstPeriod = sortedBenefits[0]?.periodMo ?? 1;
          const secondPeriod = sortedBenefits[1]?.periodMo ?? firstPeriod;
          if (secondPeriod > firstPeriod) {
            durationValue = getMultiOfferDuration(firstPeriod + 1, secondPeriod);
          }
        } else if (maxPeriodMo && maxPeriodMo > 1) {
          durationValue = getMultiOfferDuration(2, maxPeriodMo);
        }
      }

      if (durationValue) {
        resolvedContent = resolvedContent.replace(
          new RegExp(`\\[${durKey}\\]`, "g"),
          durationValue
        );
      }
    });
  }

  // 4) Single‑benefit cleanup (keep existing behavior)
  if (consideredBenefits?.length === 1) {
    resolvedContent = resolvedContent?.replace(
      /;\s*\$[^.]*mos\.[^.]*\.[ \t]*/gi,
      ". "
    );

    // Strip only continuation segments inside a parenthetical that still
    // contain an unresolved [token]. Preserves any segment whose tokens were
    // fully resolved (e.g. "(...for 1st mo)" must survive even when a
    // later [second_offer_duration] elsewhere triggered this branch).
    resolvedContent = resolvedContent?.replace(
      /;\s*[^)]*\[[^\]]+\][^)]*(?=\))/g,
      ""
    );
    // Optional: collapse spaces
    resolvedContent = resolvedContent?.replace(/\s{2,}/g, " ");
  }

  return resolvedContent;
};
// Generated by Copilot- End - 2026-02-25 13:32:58

/**
 * Shared resolver for HIGH‑level + LOW‑level placeholders.
 * High-level: [total_offers_prices_durations], [benefits_prices_durations]
 * Low-level:  *_req_price, *_offer_duration, [first_offer], [second_offer]
 */
const resolveAllOfferPlaceholders = (
  {
    content,
    processedTotalOffersPrices,
    offerLegalTail,
    productNameKey,
    productType,
    offerPriceNum,
    geminiFeeNum,
    sortedBenefits,
    maxPeriodMo,
    consideredBenefits,
    card,
    evaluateReqPriceFormula
  }: {
    content: string;
    processedTotalOffersPrices: string;
    offerLegalTail: string;
    productNameKey: string;
    productType: string;
    offerPriceNum: number;
    geminiFeeNum: number;
    sortedBenefits: Array<{ discount: number; periodMo: number; code: string }>;
    maxPeriodMo: number | null;
    consideredBenefits: Array<{ discount: number; periodMo: number; code: string }>;
    card: any;
    evaluateReqPriceFormula: (
      priceKey: string,
      requiredPriceEvalContext: {
        baseOffer: number;
        deviceFee: number;
        useRsnFeeLocal: boolean;
      },
      consideredBenefits: Array<{ code: string; discount: number; periodMo: number }>,
      tierLevel?: number
    ) => { basePriceExcludingRsn: number; includesRsnFee: boolean };
  }
): string => {
  let newContent = content ?? "";

  const hasAnyPlaceholder = /\[([^\]]+)\]/.test(newContent);
  if (!hasAnyPlaceholder) {
    return newContent;
  }

  if (newContent.includes("[total_offers_prices_durations]")) {
    newContent = newContent.replace(
      /\[total_offers_prices_durations\]/g,
      processedTotalOffersPrices
    );
  }
  if (newContent.includes("[benefits_prices_durations]")) {
    newContent = newContent.replace(
      /\[benefits_prices_durations\]/g,
      offerLegalTail
    );
  }

  newContent = applyOfferPlaceholders({
    content: newContent,
    productNameKey,
    productType,
    offerPriceNum,
    geminiFeeNum,
    sortedBenefits,
    maxPeriodMo,
    consideredBenefits,
    card,
    evaluateReqPriceFormula
  });

  return newContent;
}

const getTopperMsg = (
  packageConfigOffersVariant,
  benefitsArr: { discount: number; periodMo: number; code: string }[],
  totalOfferDiscount: number | null,
  maxPeriodMo: number | null
) => {
  if (!packageConfigOffersVariant?.topperMsg) {
    return null;
  }

  const topperCfg = Array.isArray(packageConfigOffersVariant?.topperMsg) ?
    packageConfigOffersVariant?.topperMsg[0] :
    packageConfigOffersVariant?.topperMsg;

  if (!topperCfg) {
    return null;
  }

  const cfg = topperCfg?.config ?? {};

  const offerScope = cfg?.offerScope;       // expected: "FIRST_OFFER" | "COMBINED" | "DEFAULT"
  const durationScope = cfg?.durationScope; // expected: "FIRST_MONTH" | "SHORTEST_DURATION" | "FULL_DURATION" | "DEFAULT"

  const sorted = [...(benefitsArr || [])].sort(
    (a, b) => (a?.periodMo || 0) - (b?.periodMo || 0)
  );

  const sumDiscount = (list: typeof benefitsArr) => list?.reduce((s, b) => s + (Number(b?.discount) || 0), 0);

  const shortestDuration = (list: typeof benefitsArr) => list?.reduce((min, b) => {
    const p = Number(b?.periodMo) || 0;
    return min === 0 || (p > 0 && p < min) ? p : min;
  }, 0);

  let discountValue = 0;
  let durationMonths = 0;

  // ----- OFFER SCOPE -----
  if (offerScope === "FIRST_OFFER") {
    const first = sorted[0];
    const firstDiscount = Number(first?.discount) || 0;
    const firstPeriod = Number(first?.periodMo) || 1;

    discountValue = firstDiscount;

    // ----- DURATION SCOPE for FIRST_OFFER -----
    if (durationScope === "FIRST_MONTH") {
      durationMonths = 1;
    } else if (durationScope === "SHORTEST_DURATION") {
      durationMonths = Math.max(firstPeriod, 1);
    } else {
      // "FULL_DURATION", "DEFAULT", undefined, or anything else
      durationMonths = Math.max(firstPeriod, 1);
    }
  } else if (offerScope === "COMBINED") {
    const combinedDiscount = sumDiscount(sorted);
    const shortest = shortestDuration(sorted);
    const full = Number(maxPeriodMo) || 0;

    if (durationScope === "FIRST_MONTH") {
      discountValue = combinedDiscount;
      durationMonths = 1;
    } else if (durationScope === "SHORTEST_DURATION") {
      const shortestMonths = shortest || 1;
      discountValue = sorted?.reduce((sum, b) => {
        const d = Number(b?.discount) || 0;
        return sum + d * shortestMonths;
      }, 0);
      durationMonths = shortestMonths;
    } else {
      // "FULL_DURATION", "DEFAULT", undefined, or anything else
      discountValue = Number(totalOfferDiscount) || combinedDiscount;
      durationMonths = full || shortest || 1;
    }
  } else {
    // DEFAULT / fallback when offerScope is "DEFAULT" or anything else / missing
    if (sorted.length === 1) {
      // Single offer: use total benefit over its full duration
      // e.g. 10 off 3 months -> 30 off for 3 months
      const first = sorted[0];
      const d = Number(first?.discount) || 0;
      const p = Number(first?.periodMo) || 0;

      discountValue = d * p;
      durationMonths = p || 1;
    } else {
      // Multiple offers: use 1st‑month combined discount
      // e.g. 35 off 1m + 5 off 24m -> 40 off for 1st month
      discountValue = sumDiscount(sorted); // sum of all discounts
      durationMonths = 1;
    }
  }

  if (!discountValue || discountValue < 0) {
    discountValue = 0;
  }
  if (!durationMonths || durationMonths < 0) {
    durationMonths = 1;
  }

  const topperTemplate =
    durationMonths === 1 ?
      topperCfg?.firstMonthOnly :
      topperCfg?.totalDuration;

  // Generated by Copilot- Start - 2026-04-20 17:30:45
  // Normalize spanText: Storyblok may deliver it as an array of components
  const spanTextRaw = topperCfg?.spanText;
  const spanTextCfg = Array.isArray(spanTextRaw) ? spanTextRaw[0] : spanTextRaw;
  const spanColor = topperCfg?.spanColor;
  const resolvedTemplate = (topperTemplate || "").replace(/\[durationMonths\]/g, String(durationMonths));

  let resolvedSpanText: { content: string; color?: string } | null = null;
  let beforeText: string | null = null;
  let mainContent: string;

  if (spanTextCfg) {
    // New format: explicit spanText config (object or first element of array)
    const spanContent = (spanTextCfg.content || "").replace(/\$\[discount\]/g, `$${discountValue}`);
    resolvedSpanText = {
      content: spanContent,
      color: spanTextCfg.color
    };

    if (resolvedTemplate.includes("$[discount]")) {
      // Template still has discount token — split: beforeText + [span] + mainContent(suffix)
      // Use slice(1).join() to correctly handle templates with multiple $[discount] tokens
      const parts = resolvedTemplate.split("$[discount]");
      beforeText = parts[0] || null;
      mainContent = parts.slice(1).join(`$${discountValue}`);
    } else {
      // Template is already suffix-only (no discount token)
      mainContent = resolvedTemplate;
    }
  } else if (spanColor && resolvedTemplate.includes("$[discount]")) {
    // Old format with spanColor: auto-split on $[discount] → beforeText + span + content
    // Use slice(1).join() to correctly handle templates with multiple $[discount] tokens
    const parts = resolvedTemplate.split("$[discount]");
    beforeText = parts[0] || null;
    resolvedSpanText = {
      content: `$${discountValue}`,
      color: spanColor
    };
    mainContent = parts.slice(1).join(`$${discountValue}`);
  } else {
    // Old format without spanColor: single-color string
    mainContent = resolvedTemplate.replace(/\$\[discount\]/g, `$${discountValue}`);
  }
  // Generated by Copilot- End - 2026-04-20 17:30:45

  return {
    content: mainContent,
    beforeText,
    spanText: resolvedSpanText,
    color: topperCfg.color || topperCfg.fontColor || null,
    discountValue
  };
};

export const planCardAdapter = ({
  content,
  getOffersData,
  getOffersDTVsData,
  packageConfig,
  index,
  cms_type,
  embeddedSvods: embeddedSvodsArg,
  dtviEmbeddedSvodOffers,
  dtvsEmbeddedSvodOffers,
  cmsPageName,
  byodPlanCardOffers,
  affiliatePartner,
  genreSvodOffers
}: IPlanCardAdapter): any => {
  let embeddedSvods = embeddedSvodsArg;
  try {
    logAdapterEvent(ADAPTER_NAME, "starting plan card adaption", {
      index,
      cms_type,
      cmsPageName
    });
    const componentType =
      content?.variant === "toggle-package-card" ?
        ComponentNames.TOGGLE_PLAN_CARD :
        content?.type === "PLANCARD" && content?.orientation === "horizontal" ?
          ComponentNames.HORIZONTAL_PLAN_CARD :
          ComponentNames.PLANCARD;

    const isProgressivePkgCard =
      content?.packageCardVariant === "progressive-package-card";

    const isProductToggleVariant =
      componentType === ComponentNames.TOGGLE_PLAN_CARD &&
      content?.cards?.some(card => Object.prototype.hasOwnProperty.call(card, "toggle2PlanCardData")) &&
      content?.cardsNoLocalChannels?.length > 0;

    const isLocalsToggleOnlyVariant =
      !isProductToggleVariant && Array.isArray(content?.toggle?.toggles);

    // const rrEnabled = disableArsFee;
    const productType = content?.productType;
    // Prefer BYOD PlanCard specific offers if productType is byod
    const effectiveOffersData = productType === 'byod' && byodPlanCardOffers?.offerPrices ? byodPlanCardOffers : getOffersData;
    // Device (Gemini) fee is resolved upstream by the DTVI resolver (live video-device
    // price, else CMS dtviDeviceFee) and carried on getOffersData.geminiFee — read it
    // directly here, no fallback repeated. BYOD has no device fee.
    const globalGeminiFee = productType === 'byod' ? 0 : (parseFloat(effectiveOffersData?.geminiFee) || 0);
    const useGeminiFee = productType !== "byod";

    /**
     * @description Choose local/non-local (dtvs), or regular/byod (dtvi) offers array
     * @param isSatellite choose satellite (dtvs) or internet (dtvi) price structure
     * @param isNoLocals if satellite is chosen, choose to get with or without locals pricing
     */
    const chooseOffersData = (isSatellite: boolean, isNoLocals) => {
      if (isSatellite) {
        const satelliteOffers =
          getOffersDTVsData?.offerPrices ||
          baseSatelliteOffersData_RR.offerPrices;

        return satelliteOffers?.map?.(item => {
          if (
            !isNoLocals &&
            item?.localPrice &&
            Object.entries(item.localPrice).length > 0
          ) {
            const [key, value] = Object.entries(item.localPrice)[0]; // Extract key-value pair
            // satellite has no benefits to add as of 6/15/25, so don't need to spread item
            return { [key]: value }; // Move key-value pair to the parent object
          }
          return item;
        });
      } else if (productType === "byod") {
        const byodOffers = effectiveOffersData?.offerPrices ?
          effectiveOffersData?.offerPrices :
          baseInternetOffersData_RR.offerPrices;

        return byodOffers?.map?.(item => {
          if (item?.byodPrice) {
            const [key, value] = Object.entries(item.byodPrice)[0]; // Extract key-value pair
            return {
              // Move key-value pair to the parent object
              ...item, // spread to retain the "benefits" KV
              [key]: value
            };
          }
          return item;
        });
      }
      return (
        effectiveOffersData?.offerPrices || baseInternetOffersData_RR.offerPrices
      );
    };

    /**
     * Evaluate required price formulas for plan cards dynamically.
     * RSN fee is included based on useRsnFeeLocal flag.
     * Returns base price excluding RSN (handled client-side) and RSN flag.
     */
    const evaluateReqPriceFormula = (
      _priceKey: string,
      requiredPriceEvalContext: {
        baseOffer: number;
        deviceFee: number;
        useRsnFeeLocal: boolean;
      },
      consideredBenefits: Array<{ code: string; discount: number; periodMo: number }>,
      tierLevel: number = 0
    ) => {
      const { baseOffer, deviceFee, useRsnFeeLocal } = requiredPriceEvalContext;

      const basePrice = baseOffer + deviceFee;

      const sortedBenefits = [...(consideredBenefits || [])].sort(
        (a, b) => (a.periodMo || 0) - (b.periodMo || 0)
      );

      let calculatedPrice = basePrice;

      for (let i = 0; i < tierLevel && i < sortedBenefits.length; i += 1) {
        const discount = sortedBenefits[i]?.discount ?? 0;
        calculatedPrice += discount;
      }

      const includesRsnFee = useRsnFeeLocal;

      return {
        basePriceExcludingRsn: calculatedPrice,
        includesRsnFee
      };
    };

    /**
     * @description Inject Offer Price, static copy prices, modified channel count
     * - clean up card data.
     * - Create initial price map with static values
     * @params { cards, isCardSetSatellite, isNoLocals }
     * @returns new cards
     */
    const injectStaticDataAndClean = ({
      cards,
      isCardSetSatellite,
      isNoLocals
    }) => {
      try {
        logAdapterEvent(ADAPTER_NAME, "Injecting static data", {
          cmsPageName
        })
        const offerPricesData = chooseOffersData(
          isCardSetSatellite,
          isNoLocals
        );

        // eslint-disable-next-line complexity
        return cards?.map(card => {
          // -------------------------
          let offer: any = {};
          let offerPrice: string;
          let offerPriceNum: number;
          const geminiFeeNum =
            card?.pricingData?.geminiFee || globalGeminiFee;

          /** Cleaned string */
          const productNameKey = packageNameNormalizing(
            card?.productName?.content
          );

          const packageConfigItem = packageConfig?.find(item => item?.id === card?.packageId);

          /* - Get corresponsing offer
               - populate prices
            */
          const hasNumber = /\d+(\.\d+)?/.test(
            card?.productPrice?.price?.content
          );

          if (
            !productNameKey ||
            (card?.productPrice?.price?.content && !hasNumber) ||
            card?.productPrice?.price?.content.includes("[offer_price]")
          ) {
            // Match product in offers array
            offer =
              offerPricesData?.find(offer => {
                const key = Object.keys(offer)?.[0];
                return (
                  productNameKey.includes(key) || key.includes(productNameKey)
                );
              }) || [];

            const offerKey = Object.keys(offer)?.[0];
            if (!offer?.[offerKey]){
              console.error(`Offer price not found for product: ${productNameKey}`, {
                offerPricesData,
                card,
                cmsPageName
              });
              throw new Error("Offer price not found", offer);
            }
            offerPriceNum = offer?.[offerKey];
            offerPrice = offer?.[offerKey].toFixed(2);
          } else {
            // match a hardcoded offer price (for calculations)
            const match =
              card?.productPrice?.price?.content?.match(/[\d.]+/)?.[0];
            if (match) {
              offerPriceNum = parseFloat(match);
              offerPrice = offerPriceNum.toFixed(2);
            }
          }

          /*
           * Inject static fees into copy
           * - Static Fees (geminiFee, arsFee, offer price, multi offer amounts) are injected below on SS
           * - Dynamic Fees (total prices, which include rsn fee) are calculated on client side
           */
          // convert to array for consistency
          if (
            card?.productPrice?.priceCopy !== undefined &&
            !Array.isArray(card?.productPrice?.priceCopy) &&
            Object.keys(card?.productPrice?.priceCopy).length > 0
          ) {
            card.productPrice.priceCopy = [card.productPrice.priceCopy];
          }

          const modifiedOfferPrice =
            card?.productPrice?.price?.content?.replace(
              "[offer_price]",
              offerPrice
            );

          let firstMonthDiscount: number | null = null;
          let totalOfferDiscount: number | null = null;
          let maxPeriodMo: number | null = null;
          let topperMsgModded: string | null = null;
          let topperResult: { content: string; beforeText?: string | null; spanText: { content: string; color?: string } | null; discountValue?: number; color?: string | null } | null = null;
          const legalText: string | null = null;
          const moddedPriceCopy = [];

          const packageConfigOfferVariants = packageConfigItem?.offers?.[0];
          const isSatelliteFlag = content?.isSatellite === true || content?.isSatellite === 'true';
          let packageConfigOffersVariant = isSatelliteFlag ?
            packageConfigOfferVariants?.satellite :
            (productType === 'byod' ? packageConfigOfferVariants?.byod : packageConfigOfferVariants?.internet);
          if (affiliatePartner) {
            packageConfigOffersVariant = packageConfigOfferVariants?.internet_affiliates?.[affiliatePartner];
          }

          let benefitsPricesDuration = packageConfigOffersVariant?.packageLegal?.benefits_prices_durations || "";
          let totalOffersPricesDuration = packageConfigOffersVariant?.packageLegal?.total_offers_prices_durations || "";
          const consideredBenefits = (offer?.benefits || [])
            .filter(
              b => b &&
                  typeof b.discount === "number" &&
                  typeof b.periodMo === "number" &&
                  typeof b.code === "string"
            )
            .map(({ discount, periodMo, code }) => ({
              discount,
              periodMo,
              code
            }));
          if (offer?.benefits) {
            const benefitsArr = (offer?.benefits || []).map(b => ({
              discount:
                typeof b.discount === "number" ?
                  b.discount :
                  parseFloat(b.discount) || 0,
              periodMo: b.periodMo,
              code: b.code
            }));
            maxPeriodMo = benefitsArr.reduce((max, b) => {
              const p = Number(b.periodMo) || 0;
              return p > max ? p : max;
            }, 0);

            const sortedBenefits = [...consideredBenefits].sort(
              (a, b) => a.periodMo - b.periodMo
            );

            totalOfferDiscount = sortedBenefits.reduce((acc, b) => {
              const d = Number(b.discount) || 0;
              const p = Number(b.periodMo) || 0;
              return acc + d * p;
            }, 0);

            firstMonthDiscount = sortedBenefits.reduce(
              (sum, b) => sum + (b.discount ?? 0),
              0
            );

            topperResult = getTopperMsg(
              packageConfigOffersVariant,
              sortedBenefits,
              totalOfferDiscount,
              maxPeriodMo
            );
            topperMsgModded = topperResult ? topperResult.content : null;

            if (consideredBenefits.length === 1 && benefitsPricesDuration) {
              benefitsPricesDuration = benefitsPricesDuration.replace(/\.;\s*\$\[second_offer\][^\n]*/gi, "");
            }
            if (consideredBenefits.length === 1 && totalOffersPricesDuration) {
              // Strip only the continuation segment(s) inside a parenthetical
              // that still hold an unresolved [*] token. Preserves segments
              // whose tokens belong to the resolved first offer.
              totalOffersPricesDuration = totalOffersPricesDuration.replace(
                /;\s*[^)]*\[[^\]]+\][^)]*(?=\))/g, ""
              );
            }

            const offerLegalTail = getOfferLegal(consideredBenefits, benefitsPricesDuration);

            // Reuse low-level resolver for total_offers template
            const processedTotalOffersPrices = applyOfferPlaceholders({
              content: totalOffersPricesDuration,
              productNameKey,
              productType,
              offerPriceNum,
              geminiFeeNum,
              sortedBenefits,
              maxPeriodMo,
              consideredBenefits,
              card,
              evaluateReqPriceFormula
            });

            // PRICE COPY LOOP: use single high+low resolver
            for (const copyObj of card?.productPrice?.priceCopy || []) {
              if (!(copyObj?.content || copyObj?.cta)) {
                continue;
              }

              const newContent = copyObj?.content;
              const hasPlaceholders = newContent && /\[([^\]]+)\]/.test(newContent);
              if (!hasPlaceholders) {
                moddedPriceCopy.push({
                  ...copyObj,
                  ciContent: copyObj?.content || "",
                  content: newContent || ""
                });
                continue;
              }

              const finalContent = resolveAllOfferPlaceholders({
                content: newContent,
                processedTotalOffersPrices,
                offerLegalTail,
                productNameKey,
                productType,
                offerPriceNum,
                geminiFeeNum,
                sortedBenefits,
                maxPeriodMo,
                consideredBenefits,
                card,
                evaluateReqPriceFormula
              });
              moddedPriceCopy.push({
                ...copyObj,
                ciContent: copyObj?.content,
                content: finalContent
              });
            }

            if (Array.isArray(card.productPrice.priceCopy)) {
              card.productPrice.priceCopy = [];
            }
          }

          const strikeoutPriceNumber =
            offerPriceNum !== undefined && offerPriceNum !== null ?
              offerPriceNum + (firstMonthDiscount || 0) :
              undefined;
          const modifiedStrikeoutPrice =
            strikeoutPriceNumber !== undefined ?
              card?.productPrice?.strikeoutPrice?.content?.replace(
                "[strikeout_price]",
                strikeoutPriceNumber.toFixed(2)
              ) :
              card?.productPrice?.strikeoutPrice?.content;
          const moddedProductName = card?.productName?.content?.trim();
          const productKey = moddedProductName
            ?.split("<br>")[0]
            ?.replace(/<[^>]+>/g, "")
            ?.replace(/™/g, "")
            ?.toLowerCase();
          const normalizedProductKey = productKey
            ?.normalize("NFD")
            ?.replace(/[\u0300-\u036f]/g, "")
            ?.replace(/\s+/g, "");
          const moddedChannelCount = getChannelCount(
            normalizedProductKey,
            packageConfig,
            isSatelliteFlag
          );

          /** For dynamic client side calculation */
          const pricingData = {
            useRsnFee: ![
              "ENTERTAINMENT",
              "MÁS LATINO™",
              "MAS LATINO",
              "ÓPTIMO MÁS™",
              "OPTIMO MAS"
            ]?.some(name => productNameKey.includes(name)),
            rsnFee: card?.rsnFee || null, // null = value is dynamic
            offerSubtotal: offerPriceNum + (useGeminiFee ? geminiFeeNum : 0),
            extOfferSubtotal:
              !isCardSetSatellite && Array.isArray(offer?.benefits) ?
                offerPriceNum +
                  (useGeminiFee ? geminiFeeNum : 0) +
                  offer?.benefits?.[0]?.discount :
                null,
            requiredCalc: {
              requiredPriceValuesNoRsn: (card as any)._requiredPriceValuesNoRsn || {},
              reqPricePlaceholdersWithRsn: (card as any)._reqPricePlaceholdersWithRsn || []
            }
          };

          /* Spread in formatted content */
          return {
            ...card,
            productName: {
              ...card?.productName,
              content: moddedProductName
            },
            productPrice: {
              ...card?.productPrice,
              price: {
                ...card?.productPrice?.price,
                moddedContent:
                  modifiedOfferPrice || card?.productPrice?.price?.content
              },
              strikeoutPrice: {
                ...card?.productPrice?.strikeoutPrice,
                moddedContent: modifiedStrikeoutPrice || card?.productPrice?.strikeoutPrice?.content
              },
              ...(moddedPriceCopy?.length > 0 && {
                priceCopy: moddedPriceCopy
              })
            },
            topper: {
              content: (() => {
                const { content: _c, discountValue: _d, ...rawRest } = (topperResult || {}) as NonNullable<typeof topperResult>;
                const rest = Object.fromEntries(
                  Object.entries(rawRest).filter(([, v]) => v != null)
                );
                const rawContent = topperMsgModded ?? card?.topper?.content?.content;
                const resolvedContent = _d != null ?
                  (rawContent || "").replace(/\$\[discount\]/g, `$${_d}`) :
                  rawContent;
                return {
                  ...card?.topper?.content,
                  ...rest,
                  content: resolvedContent
                };
              })()
            },
            pricingData,
            ...(card?.seeAllChannelsCta && {
              seeAllChannelsCta: {
                ...card?.seeAllChannelsCta,
                label: (card?.seeAllChannelsCta?.label || "").replace(
                  /\${channelCount\}/g,
                  moddedChannelCount
                )
              }
            }),
            ...(Array.isArray(card?.features) &&
              card?.features?.length > 0 && {
              features: [
                {
                  ...card.features[0],
                  content: card?.features?.[0]?.content?.replace(
                    /\${channelCount}/g,
                    moddedChannelCount
                  )
                },
                ...card.features.slice(1)
              ]
            }),
            ...(card?.comparePackagesCta && {
              comparePackagesCta: {
                ...card?.comparePackagesCta,
                analytics: {
                  linkName: card?.comparePackagesCta?.label?.toLowerCase(),
                  linkPosition: "panel_plan card",
                  linkDestination: "/modal/compare packages"
                }
              }
            }),
            actualSeeAllChannelsLabel: card?.seeAllChannelsCta?.label,
            featuresChnlCount: card?.features?.[0]?.content,
            totalOfferDiscount,
            topperMsgModded,
            legalText,
            consideredBenefits,
            queryparam: packageConfigItem?.queryparam
          };
        });
      } catch (error) {
        logError(ADAPTER_NAME, error, {
          index,
          cms_type,
          cmsPageName,
          step: "Error injecting static data and cleaning cards:"
        });
        return cards;
      }
    };

    /* --> Evaluation start */
    let updtdContent =
      cms_type === "aem" ?
        new ReportingService().fixAnalyticsValues(
          content,
          ReportingDefaults.dtvPlanCard,
          ComponentNames.PLANCARD,
          index
        ) :
        new ReportingService().fixAnalyticsValues(
          content,
          ReportingDefaults.dtvPlanCard,
          ComponentNames.PLANCARD,
          index,
          null,
          "STORYBLOK"
        );

    /* Call injectStaticPricesAndClean and create cardData */
    const cardData = {
      locals: injectStaticDataAndClean({
        cards: updtdContent?.cards,
        isCardSetSatellite: isProductToggleVariant ?
          false :
          Boolean(updtdContent?.isSatellite),
        isNoLocals: false
      }),

      // satellite condition is for if user is locals ineligible
      ...((isLocalsToggleOnlyVariant || updtdContent?.isSatellite) && {
        noLocals: injectStaticDataAndClean({
          cards: isLocalsToggleOnlyVariant ?
            updtdContent?.cardsNoLocalChannels?.length > 0 ?
              updtdContent?.cardsNoLocalChannels :
              updtdContent?.cards?.map(card => ({
                ...card,
                ...card?.toggle2PlanCardData
              })) :
            updtdContent?.cards,
          isCardSetSatellite: isProductToggleVariant ?
            false :
            Boolean(updtdContent?.isSatellite),
          isNoLocals: true
        })
      }),
      ...(isProductToggleVariant && {
        satelliteLocals: injectStaticDataAndClean({
          cards: updtdContent?.cards?.map(card => ({
            ...card,
            ...card?.toggle2PlanCardData
          })),
          isCardSetSatellite: true,
          isNoLocals: false
        }),
        satelliteNoLocals: injectStaticDataAndClean({
          cards: updtdContent?.cardsNoLocalChannels,
          isCardSetSatellite: true,
          isNoLocals: true
        })
      })
    };
    /** --------------------------------------------------- */
    /** Filter out unused cards, cardsNoLocalChannels to reduce packet size */
    if (cardData?.locals?.length > 0) {
      updtdContent = Object.keys(updtdContent).reduce((newData, key) => {
        if (!["cards", "cardsNoLocalChannels"].includes(key)) {
          newData[key] = updtdContent[key];
        }
        return newData;
      }, {});
    }
    /** --------------------------------------------------- */
    /** If the isSearchZipCode SB feed value false OR no dynamic fees exist, hide zipcode module */
    const hasDynamicRsnFee = Object.keys(cardData).some(key => {
      const cards = cardData[key];
      return isZipcodeDependentCardPrices(cards, false);
    });
    const showTopperZipcode =
      updtdContent?.isSearchZipCode !== undefined ?
        updtdContent?.isSearchZipCode === true || hasDynamicRsnFee :
        hasDynamicRsnFee;
    /** --> Evaluation end */
    const isSatellite =
      content?.isSatellite === true || content?.isSatellite === "true";
    embeddedSvods = addPackageDetailsToEmbeddedSvods({
      embeddedSvods,
      isSatellite,
      epochSvodsData: isSatellite ?
        dtvsEmbeddedSvodOffers :
        dtviEmbeddedSvodOffers,
      packageConfig,
      componentType: ComponentNames.PLANCARD,
      excludedSvods: content?.excludedSvods || []
    });

    const cmpPkgPrice = [];
    if (content?.comparePkgTray) {
      getOffersData?.offerPrices?.forEach(offer => {
        const key = Object.keys(offer)?.[0];
        cmpPkgPrice.push({ [key]: productType === "byod" ? offer?.byodPrice?.[key] : offer?.[key] });
      });
    }
    const resolvedOffersData = content?.comparePkgTray ?
      { offerPrices: cmpPkgPrice } :
      getOffersData;
    return {
      props: {
        ...updtdContent,
        comparePkgTray: content?.comparePkgTray ?
          getComparePkgTrayData({
            comparePkgTray: content.comparePkgTray,
            packageConfig,
            genreSvodOffers,
            getOffersData: resolvedOffersData
          }) :
          null,
        embeddedSvods: embeddedSvods || [],
        cards: cardData,
        config: {
          showTopperZipcode,
          isProgressivePkgCard,
          isProductToggleVariant,
          isLocalsToggleOnlyVariant,
          globalGeminiFee,
          useGeminiFee
          // arsFee,
          // useArsFee,
        },
        options: {
          id: updtdContent?.id,
          ...updtdContent?.options
        }
      },
      type: componentType
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      index,
      cms_type,
      cmsPageName
    });
    return {
      props: {
        error: error?.message,
        type: ComponentNames.PLANCARD,
        options: {
          id: `ERROR-dtv-plan-card-${index}`
        }
      }
    };
  }
};

export default planCardAdapter;
